import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { CheckCircle2, MessageSquareWarning } from 'lucide-react';
import apiClient from '@/services/apiClient';
import { useAuthStore } from '@/stores/useAuthStore';
import { useTenantStore } from '@/stores/useTenantStore';
import {
  getBufferedLogs,
  installConsoleLogBuffer,
} from '@/lib/consoleLogBuffer';
import { Button } from '@/components/ui/button';
import { TextInput } from '@/components/ui/text-input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { useTranslation } from '@/hooks/useTranslation';
import { useFloatingClearance } from '@/hooks/useFloatingClearance';
import { useFeedbackStore } from '@/stores/useFeedbackStore';

const collectClientContext = () => {
  if (typeof window === 'undefined') return {};
  const w = window;
  return {
    pageUrl: w.location.href,
    referrer: document.referrer || undefined,
    viewport: `${w.innerWidth}x${w.innerHeight}`,
    screenSize: `${w.screen.width}x${w.screen.height}`,
    language: navigator.language,
    timezone:
      typeof Intl !== 'undefined'
        ? Intl.DateTimeFormat().resolvedOptions().timeZone
        : undefined,
  };
};

export const FeedbackWidget: React.FC = () => {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const tenantConfig = useTenantStore((s) => s.publicConfig);

  // Open state lives in a shared store so surfaces without a FAB (the
  // /assistant workspace) can trigger the same dialog from their chrome.
  const open = useFeedbackStore((s) => s.open);
  const setOpen = useFeedbackStore((s) => s.setOpen);
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Dodge any sticky bottom action bar (the encounter "Save & sign" footer)
  // or other bottom-anchored dock, matching the MaieLauncherPill so the two
  // floating controls lift together and sit ABOVE the bar.
  const { ref: fabRef, lift: bottomLift } = useFloatingClearance<HTMLButtonElement>();

  // The /assistant workspace fills the viewport with its own chrome and
  // pins a full-width composer to the bottom edge — there's no free
  // corner for a floating FAB at ANY breakpoint (it lands on the
  // composer's action row). The workspace hides this FAB and opens the
  // same dialog from a button in its header via the feedback store.
  const isAssistantWorkspace = useLocation().pathname.startsWith('/assistant');

  useEffect(() => {
    installConsoleLogBuffer();
  }, []);

  // Pre-fill the email from the signed-in user when the widget opens.
  useEffect(() => {
    if (open && !email && user?.email) setEmail(user.email);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const reset = () => {
    setMessage('');
    setEmail(user?.email ?? '');
    setError(null);
    setSuccess(false);
  };

  const handleClose = () => {
    setOpen(false);
    setTimeout(reset, 250);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim() || busy) return;
    setBusy(true);
    setError(null);
    const ctx = collectClientContext();
    try {
      await apiClient.post('/landing-feedback', {
        message: message.trim(),
        email: email.trim() || undefined,
        ...ctx,
        logs: getBufferedLogs(),
        meta: {
          submittedAt: new Date().toISOString(),
          source: 'maie-emr',
          userId: user?.id ?? null,
          tenantId: tenantConfig?.tenantId ?? null,
          tenantSlug: tenantConfig?.slug ?? null,
          pixelRatio: window.devicePixelRatio,
          online: typeof navigator !== 'undefined' ? navigator.onLine : null,
        },
      });
      setSuccess(true);
    } catch (err) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const anyErr = err as any;
      setError(
        anyErr?.response?.data?.message ??
          (err instanceof Error ? err.message : t('feedback.errors.send')),
      );
    } finally {
      setBusy(false);
    }
  };

  // Themed input chrome — reused across both message textarea and email
  // input so they stay coherent in light/dark and pick up the tenant
  // accent on focus.
  const inputStyle: React.CSSProperties = {
    backgroundColor: 'var(--color-bg-primary)',
    borderColor: 'var(--color-border-primary)',
    color: 'var(--color-text-primary)',
  };

  return (
    <>
      {/* Compact icon button anchored bottom-start (logical, so it
          mirrors with RTL automatically). Themed against the tenant
          primary palette so it matches the EMR's brand instead of a
          flat black pill.
          On mobile we lift it above the encounter MobileMiniBar
          (`fixed inset-x-3 bottom-3 z-40` in RecordingDock.tsx) and
          drop its z-index below it so the recording bar always wins
          when both are on-screen. Desktop keeps the original
          bottom-4 placement.
          At `lg`, where the desktop sidebar is visible, we shift the
          button past it (MainLayout publishes `--app-sidebar-width`,
          tracking collapse) so it sits in the content area instead of
          overlaying the sidebar's "Powered by" foot. The var is unset
          off MainLayout (public routes) → falls back to the start-4
          placement. */}
      <button
        ref={fabRef}
        type="button"
        onClick={() => setOpen(true)}
        title={t('feedback.title')}
        aria-label={t('feedback.title')}
        className={`feedback-fab fixed bottom-6 start-4 z-30 ${
          isAssistantWorkspace ? 'hidden' : 'inline-flex'
        } items-center justify-center min-h-11 min-w-11 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2`}
        style={{
          backgroundColor: 'var(--color-primary-600)',
          color: 'var(--color-text-button)',
          boxShadow: 'var(--shadow-lg)',
          marginBottom: 'env(safe-area-inset-bottom, 0)',
          // Lift to clear the bottom bar/docks (mirrors MaieLauncherPill).
          // No transform transition so it tracks scroll exactly.
          transform: bottomLift > 0 ? `translateY(-${bottomLift}px)` : undefined,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = 'var(--color-primary-700)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'var(--color-primary-600)';
        }}
      >
        <MessageSquareWarning className="w-4 h-4" />
      </button>

      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (!next) handleClose();
        }}
      >
        <DialogContent maxWidthClassName="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('feedback.title')}</DialogTitle>
            <DialogDescription>{t('feedback.subtitle')}</DialogDescription>
          </DialogHeader>

          {success ? (
            <div className="px-6 py-8 text-center space-y-3">
              <div
                className="mx-auto w-12 h-12 rounded-full flex items-center justify-center"
                style={{
                  backgroundColor:
                    'color-mix(in srgb, var(--color-success) 15%, transparent)',
                  color: 'var(--color-success)',
                }}
              >
                <CheckCircle2 className="w-6 h-6" />
              </div>
              <h3
                className="text-base font-semibold"
                style={{ color: 'var(--color-text-primary)' }}
              >
                {t('feedback.success.title')}
              </h3>
              <p
                className="text-sm max-w-xs mx-auto"
                style={{ color: 'var(--color-text-secondary)' }}
              >
                {t('feedback.success.body')}
              </p>
              <div className="pt-1">
                <Button type="button" onClick={handleClose}>
                  {t('feedback.success.done')}
                </Button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="px-6 pb-2 space-y-3">
              <div>
                <label
                  htmlFor="emr-feedback-message"
                  className="block text-xs font-semibold mb-1"
                  style={{ color: 'var(--color-text-secondary)' }}
                >
                  {t('feedback.message.label')}
                </label>
                <Textarea
                  id="emr-feedback-message"
                  required
                  autoFocus
                  rows={5}
                  maxLength={5000}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={t('feedback.message.placeholder')}
                  className="text-sm leading-relaxed"
                />
              </div>

              <div>
                <label
                  htmlFor="emr-feedback-email"
                  className="block text-xs font-semibold mb-1"
                  style={{ color: 'var(--color-text-secondary)' }}
                >
                  {t('feedback.email.label')}{' '}
                  <span
                    className="font-normal"
                    style={{ color: 'var(--color-text-tertiary)' }}
                  >
                    {t('feedback.email.optional')}
                  </span>
                </label>
                <TextInput
                  id="emr-feedback-email"
                  type="email"
                  autoComplete="email"
                  maxLength={320}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('feedback.email.placeholder')}
                  style={inputStyle}
                />
              </div>

              {error && (
                <div
                  className="text-xs rounded-md px-3 py-2"
                  role="alert"
                  style={{
                    color: 'var(--color-error)',
                    backgroundColor:
                      'color-mix(in srgb, var(--color-error) 10%, transparent)',
                    border:
                      '1px solid color-mix(in srgb, var(--color-error) 30%, transparent)',
                  }}
                >
                  {error}
                </div>
              )}

              <DialogFooter className="px-0 pb-6 pt-3">
                <Button
                  variant="outline"
                  type="button"
                  onClick={handleClose}
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  type="submit"
                  disabled={!message.trim()}
                  loading={busy}
                >
                  {t('feedback.send')}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default FeedbackWidget;
